import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { fetchAdminTimeOffList } from "@/lib/api/admin-time-off";
import { subscribeAppointmentRefresh } from "@/lib/realtime/appointment-realtime-bridge";

const UNASSIGNED_GROUP_KEY = "__unassigned__";
const UNASSIGNED_GROUP_LABEL = "Unassigned";

function asTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function rowMatchesSearch(row, query) {
  if (!query) return true;
  const haystack = [row.title, row.description, row.doctor, row.tenantName]
    .map(asTrimmedString)
    .filter(Boolean)
    .join(" · ")
    .toLowerCase();
  return haystack.includes(query);
}

function rowGroupKey(row, groupBy) {
  if (groupBy === "doctor") {
    if (row.scope === "global") {
      return { key: "__global__", label: "Global" };
    }
    const id = asTrimmedString(row.ownerId);
    if (!id) return { key: UNASSIGNED_GROUP_KEY, label: UNASSIGNED_GROUP_LABEL };
    const label = asTrimmedString(row.doctor) || "Doctor";
    return { key: id, label };
  }
  if (groupBy === "month") {
    const start = row.startAt;
    if (!start?.toFormat) return { key: UNASSIGNED_GROUP_KEY, label: UNASSIGNED_GROUP_LABEL };
    const key = start.toFormat("yyyy-LL");
    const label = start.toFormat("LLLL yyyy");
    return { key, label };
  }
  return { key: "all", label: "All entries" };
}

function sortRows(rows, sortBy) {
  const copy = [...rows];
  if (sortBy === "createdDesc") {
    copy.sort((a, b) => {
      const ac = asTrimmedString(a.createdAt);
      const bc = asTrimmedString(b.createdAt);
      return bc.localeCompare(ac);
    });
    return copy;
  }
  if (sortBy === "dateDesc") {
    copy.sort((a, b) => (b.startAt?.toMillis?.() ?? 0) - (a.startAt?.toMillis?.() ?? 0));
    return copy;
  }
  // dateAsc (default)
  copy.sort((a, b) => (a.startAt?.toMillis?.() ?? 0) - (b.startAt?.toMillis?.() ?? 0));
  return copy;
}

function groupRows(rows, groupBy) {
  const map = new Map();
  for (const row of rows) {
    const { key, label } = rowGroupKey(row, groupBy);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { key, label, rows: [] };
      map.set(key, bucket);
    }
    bucket.rows.push(row);
  }
  const groups = Array.from(map.values());
  if (groupBy === "month") {
    // Chronological order, not alphabetical (Apr 2026 must come before Aug 2026).
    groups.sort((a, b) => a.key.localeCompare(b.key));
  } else if (groupBy === "doctor") {
    groups.sort((a, b) => {
      if (a.key === "__global__") return -1;
      if (b.key === "__global__") return 1;
      if (a.key === UNASSIGNED_GROUP_KEY) return 1;
      if (b.key === UNASSIGNED_GROUP_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
  }
  return groups;
}

function mapApiItemToRow(item, { doctorIndex, tenantIndex } = {}) {
  if (!item) return null;
  const id = asTrimmedString(item.id);
  if (!id) return null;
  const startStr = asTrimmedString(item?.time?.start);
  const endStr = asTrimmedString(item?.time?.end);
  if (!startStr || !endStr) return null;
  const startAt = DateTime.fromISO(startStr, { setZone: true });
  const endAt = DateTime.fromISO(endStr, { setZone: true });
  if (!startAt.isValid || !endAt.isValid) return null;

  const ownerId = asTrimmedString(item.ownerId) || undefined;
  const tenantId = asTrimmedString(item.tenantId) || undefined;
  const doctorName = ownerId && doctorIndex ? doctorIndex.get(ownerId) || "" : "";
  const tenantName = tenantId && tenantIndex ? tenantIndex.get(tenantId) || "" : "";

  return {
    id,
    scope: asTrimmedString(item.scope) || "doctor",
    type: asTrimmedString(item.type) || "block",
    source: asTrimmedString(item.source) || "custom",
    allDay: Boolean(item.allDay),
    title: asTrimmedString(item.title) || "(untitled)",
    description: asTrimmedString(item.description) || undefined,
    ownerId,
    relatedDoctorId: asTrimmedString(item.relatedDoctorId) || undefined,
    tenantId,
    tenantName,
    doctor: doctorName || undefined,
    startAt: startAt.toLocal(),
    endAt: endAt.toLocal(),
    timezone: asTrimmedString(item?.time?.timezone) || undefined,
    importRef: item.importRef || undefined,
    customized: Boolean(item.customized),
    createdAt: asTrimmedString(item.createdAt) || undefined,
    raw: item,
  };
}

/**
 * Hook for the time-off panel. Loads admin time-off entries for `tenantId` in the
 * given date range, applies client-side filters/grouping, and exposes refresh keys.
 *
 * Errors are swallowed and exposed via `error` so non-admin viewers see an empty list
 * instead of breaking the surrounding panel.
 */
export function useTimeOffList({
  fromDate,
  toDate,
  search,
  scopeFilter = "all",
  typeFilter = "all",
  sourceFilter = "all",
  groupBy = "month",
  sortBy = "dateAsc",
  refreshKey = 0,
  doctorOptions,
  tenantOptions,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bridgeVersion, setBridgeVersion] = useState(0);

  useEffect(() => subscribeAppointmentRefresh(({ version }) => setBridgeVersion(version)), []);

  const rangeKey = useMemo(() => {
    const f = fromDate?.toUTC?.()?.toISO?.() || "";
    const t = toDate?.toUTC?.()?.toISO?.() || "";
    return `${f}|${t}|${scopeFilter}|${typeFilter}|${sourceFilter}|${refreshKey}|${bridgeVersion}`;
  }, [fromDate, toDate, scopeFilter, typeFilter, sourceFilter, refreshKey, bridgeVersion]);

  useEffect(() => {
    if (!fromDate || !toDate) {
      setItems([]);
      return undefined;
    }
    if (!fromDate.isValid || !toDate.isValid || toDate <= fromDate) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchAdminTimeOffList({
      from: fromDate.toUTC().toISO(),
      to: toDate.toUTC().toISO(),
      scope: scopeFilter !== "all" ? scopeFilter : undefined,
      type: typeFilter !== "all" ? typeFilter : undefined,
      source: sourceFilter !== "all" ? sourceFilter : undefined,
      limit: 1000,
    })
      .then((response) => {
        if (cancelled) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const indexes = useMemo(() => {
    const doctorIndex = new Map();
    if (Array.isArray(doctorOptions)) {
      for (const option of doctorOptions) {
        const id = asTrimmedString(option?.id);
        const name = asTrimmedString(option?.name);
        if (id && name) doctorIndex.set(id, name);
      }
    }
    const tenantIndex = new Map();
    if (Array.isArray(tenantOptions)) {
      for (const option of tenantOptions) {
        const id = asTrimmedString(option?.id);
        const name = asTrimmedString(option?.name);
        if (id && name) tenantIndex.set(id, name);
      }
    }
    return { doctorIndex, tenantIndex };
  }, [doctorOptions, tenantOptions]);

  const rows = useMemo(
    () => items.map((item) => mapApiItemToRow(item, indexes)).filter(Boolean),
    [items, indexes],
  );

  const groups = useMemo(() => {
    const query = asTrimmedString(search).toLowerCase();
    const matched = rows.filter((row) => rowMatchesSearch(row, query));
    const sorted = sortRows(matched, sortBy);
    return groupRows(sorted, groupBy);
  }, [rows, search, sortBy, groupBy]);

  return {
    rows,
    groups,
    totalEntries: rows.length,
    isLoading,
    error,
  };
}
